import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, Trash2, Check, Loader2, Pencil, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProjectNotes({ projectId, legacyNotes, onLegacyImported }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newText, setNewText] = useState("");
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/projects/${projectId}/notes`, { withCredentials: true });
      setNotes(data);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const addNote = async (text) => {
    const value = (text ?? newText).trim();
    if (!value) return;
    setAdding(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/projects/${projectId}/notes`,
        { text: value },
        { withCredentials: true }
      );
      setNotes(prev => [data, ...prev]);
      setNewText("");
    } finally {
      setAdding(false);
    }
  };

  const toggleDone = async (note) => {
    const next = !note.done;
    setNotes(prev => prev.map(n => n.id === note.id ? { ...n, done: next } : n));
    try {
      await axios.put(
        `${API}/admin/projects/${projectId}/notes/${note.id}`,
        { done: next },
        { withCredentials: true }
      );
    } catch {
      setNotes(prev => prev.map(n => n.id === note.id ? { ...n, done: !next } : n));
    }
  };

  const removeNote = async (id) => {
    const prev = notes;
    setNotes(p => p.filter(n => n.id !== id));
    try {
      await axios.delete(`${API}/admin/projects/${projectId}/notes/${id}`, { withCredentials: true });
    } catch {
      setNotes(prev);
    }
  };

  const startEdit = (note) => {
    setEditingId(note.id);
    setEditText(note.text);
  };

  const saveEdit = async (id) => {
    const value = editText.trim();
    if (!value) { setEditingId(null); return; }
    const { data } = await axios.put(
      `${API}/admin/projects/${projectId}/notes/${id}`,
      { text: value },
      { withCredentials: true }
    );
    setNotes(prev => prev.map(n => n.id === id ? data : n));
    setEditingId(null);
    setEditText("");
  };

  const importLegacy = async () => {
    if (!legacyNotes) return;
    const lines = legacyNotes.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
    for (const line of lines) {
      await axios.post(
        `${API}/admin/projects/${projectId}/notes`,
        { text: line },
        { withCredentials: true }
      );
    }
    await fetchNotes();
    if (onLegacyImported) onLegacyImported();
  };

  const pending = notes.filter(n => !n.done);
  const completed = notes.filter(n => n.done);

  return (
    <div className="space-y-4" data-testid="project-notes-tasks">
      {/* Legacy import banner */}
      {legacyNotes && legacyNotes.trim() && notes.length === 0 && !loading && (
        <div className="border border-[#D9A872]/40 bg-[#D9A872]/5 rounded-sm p-4 flex items-start justify-between gap-4">
          <div>
            <p className="text-[#D9A872] text-sm font-semibold mb-1">Legacy notes found</p>
            <p className="text-[#857973] text-xs whitespace-pre-wrap line-clamp-4">{legacyNotes}</p>
          </div>
          <button
            onClick={importLegacy}
            data-testid="import-legacy-notes-btn"
            className="bg-[#D9A872] text-[#0A1628] text-xs font-semibold px-3 py-2 rounded-sm hover:bg-[#D9A872]/90 whitespace-nowrap"
          >
            Import as tasks
          </button>
        </div>
      )}

      {/* Add new note */}
      <div className="flex gap-2">
        <input
          type="text"
          value={newText}
          onChange={e => setNewText(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); addNote(); } }}
          placeholder="Add a new task / note..."
          data-testid="new-note-input"
          className="flex-1 bg-[#0A1628] border border-[#1E3050] text-white px-4 py-3 rounded-sm text-sm focus:border-[#D9A872] outline-none"
        />
        <button
          onClick={() => addNote()}
          disabled={adding || !newText.trim()}
          data-testid="add-note-btn"
          className="bg-[#F59073] text-white px-4 py-3 rounded-sm text-sm font-semibold hover:bg-[#F59073]/90 disabled:opacity-50 flex items-center gap-2"
        >
          {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Add
        </button>
      </div>

      {loading && (
        <div className="text-center py-8 text-[#857973] text-sm">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
          Loading...
        </div>
      )}

      {!loading && notes.length === 0 && (
        <div className="text-center py-10 border border-dashed border-[#1E3050] rounded-sm">
          <p className="text-[#857973] text-sm">No notes yet. Start by adding one above.</p>
        </div>
      )}

      {/* Pending tasks */}
      {pending.length > 0 && (
        <div className="space-y-2">
          <p className="font-mono text-[10px] text-[#857973] tracking-wider">OPEN · {pending.length}</p>
          {pending.map(note => (
            <NoteRow
              key={note.id}
              note={note}
              editing={editingId === note.id}
              editText={editText}
              setEditText={setEditText}
              onToggle={() => toggleDone(note)}
              onDelete={() => removeNote(note.id)}
              onStartEdit={() => startEdit(note)}
              onSaveEdit={() => saveEdit(note.id)}
              onCancelEdit={() => { setEditingId(null); setEditText(""); }}
            />
          ))}
        </div>
      )}

      {/* Completed tasks */}
      {completed.length > 0 && (
        <div className="space-y-2">
          <p className="font-mono text-[10px] text-[#857973] tracking-wider">DONE · {completed.length}</p>
          {completed.map(note => (
            <NoteRow
              key={note.id}
              note={note}
              editing={editingId === note.id}
              editText={editText}
              setEditText={setEditText}
              onToggle={() => toggleDone(note)}
              onDelete={() => removeNote(note.id)}
              onStartEdit={() => startEdit(note)}
              onSaveEdit={() => saveEdit(note.id)}
              onCancelEdit={() => { setEditingId(null); setEditText(""); }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function NoteRow({ note, editing, editText, setEditText, onToggle, onDelete, onStartEdit, onSaveEdit, onCancelEdit }) {
  return (
    <div
      className={`group flex items-start gap-3 border border-[#1E3050] bg-[#0A1628] rounded-sm px-3 py-2.5 hover:border-[#F59073]/40 transition-colors ${note.done ? "opacity-60" : ""}`}
      data-testid={`note-row-${note.id}`}
    >
      <button
        onClick={onToggle}
        data-testid={`note-toggle-${note.id}`}
        className={`mt-0.5 w-5 h-5 rounded-sm border flex-shrink-0 flex items-center justify-center transition-colors ${note.done ? "bg-[#F59073] border-[#F59073]" : "border-[#1E3050] hover:border-[#F59073]"}`}
      >
        {note.done && <Check className="w-3.5 h-3.5 text-white" />}
      </button>

      {editing ? (
        <div className="flex-1 flex items-center gap-2">
          <input
            value={editText}
            onChange={e => setEditText(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") { e.preventDefault(); onSaveEdit(); }
              if (e.key === "Escape") { e.preventDefault(); onCancelEdit(); }
            }}
            autoFocus
            className="flex-1 bg-[#0A1628] border border-[#D9A872] text-white px-2 py-1 rounded-sm text-sm outline-none"
            data-testid={`note-edit-input-${note.id}`}
          />
          <button onClick={onSaveEdit} className="text-[#F59073] hover:text-white" data-testid={`note-save-${note.id}`}><Check className="w-4 h-4" /></button>
          <button onClick={onCancelEdit} className="text-[#857973] hover:text-white"><X className="w-4 h-4" /></button>
        </div>
      ) : (
        <>
          <p
            onClick={onStartEdit}
            className={`flex-1 text-sm text-white cursor-text leading-relaxed whitespace-pre-wrap break-words ${note.done ? "line-through text-[#857973]" : ""}`}
          >
            {note.text}
          </p>
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button onClick={onStartEdit} className="text-[#857973] hover:text-[#F59073] p-1" data-testid={`note-edit-${note.id}`}>
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button onClick={onDelete} className="text-[#857973] hover:text-red-400 p-1" data-testid={`note-delete-${note.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
